import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import PrimaryButton from '@/components/PrimaryButton';
import OnboardingLayout from '@/components/OnboardingLayout';
import { Svg, Path } from 'react-native-svg';
import { TempOnboardingService } from '@/services/tempOnboardingService';
import { ClinicSelectionError, clinicSelectionPayload, clinicCompletionError, clinicCompletionFeedback, ensureClinicCompletionSupport } from '@/services/onboardingClinicService';
import { ConsentService } from '@/services/consentService';
import { OnboardingStateService } from '@/services/onboardingStateService';
import { useLanguage } from '@/contexts/LanguageContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { CONSENT_PROMPT_SKIP_KEY } from '@/contexts/AuthContext';

// Password validation utility
const validatePassword = (password: string) => {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password)
  };

  const errors: string[] = [];
  if (!checks.minLength) errors.push('Mínimo 8 caracteres');
  if (!checks.hasUppercase) errors.push('Uma letra maiúscula');
  if (!checks.hasLowercase) errors.push('Uma letra minúscula');
  if (!checks.hasNumber) errors.push('Um número');

  return {
    isValid: Object.values(checks).every(Boolean),
    checks,
    errors
  };
};

export default function SetPasswordScreen() {
  const { t } = useLanguage();
  const router = useRouter();
  const params = useLocalSearchParams();
  const { userProfile, refreshProfile, waitForProfileReady } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get params from OTP verification
  const email = typeof params.email === 'string' ? params.email : '';
  const verificationToken = typeof params.verificationToken === 'string' ? params.verificationToken : '';
  const termsAccepted = params.termsAccepted === 'true';
  // hasExistingProfile no longer needed - server handles dual-path automatically

  useEffect(() => {
    // Validate required params from OTP verification
    if (!email || !verificationToken) {
      console.log('❌ Missing required params from OTP verification');
      console.log('Params received:', { 
        email: email || 'missing',
        verificationToken: verificationToken ? 'present' : 'missing',
        termsAccepted
      });
      Alert.alert(
        t('auth.setPassword.errorAccessRequired'),
        t('auth.setPassword.errorAccessRequiredMessage'),
        [{ text: 'OK', onPress: () => router.replace('/first-access') }]
      );
    } else {
      console.log('✅ Set password screen loaded with params:', {
        email,
        hasToken: !!verificationToken,
        tokenLength: verificationToken.length,
        termsAccepted
      });
      // Decode token to check expiration (for debugging only)
      try {
        const tokenParts = verificationToken.split('.');
        if (tokenParts.length === 3 && tokenParts[1]) {
          const base64 = tokenParts[1];
          const payload = JSON.parse(Buffer.from(base64, 'base64').toString());
          const expiresAt = new Date(payload.exp * 1000);
          const now = new Date();
          const timeRemaining = Math.floor((expiresAt.getTime() - now.getTime()) / 1000);
          console.log('🎫 Token info:', {
            purpose: payload.purpose,
            expiresAt: expiresAt.toISOString(),
            timeRemaining: `${timeRemaining} seconds`,
            isExpired: timeRemaining <= 0
          });
          if (timeRemaining <= 0) {
            console.warn('⚠️ Token has already expired!');
          }
        }
      } catch (e) {
        console.log('Could not decode token for debugging');
      }
    }
  }, []);


  const handleSetPassword = async () => {
    // Validate required parameters from OTP verification
    if (!email || !verificationToken) {
      Alert.alert(
        t('auth.setPassword.errorInvalidAccess'), 
        t('auth.setPassword.errorInvalidAccessMessage'),
        [{ text: 'OK', onPress: () => router.replace('/first-access') }]
      );
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      Alert.alert(t('auth.setPassword.errorTitle'), t('auth.setPassword.errorPasswordMismatch'));
      return;
    }

    // Validate password requirements
    const validation = validatePassword(password);
    if (!validation.isValid) {
      Alert.alert(
        t('auth.setPassword.errorInvalidTitle'),
        t('auth.setPassword.errorRequirements') + 
        validation.errors.map(error => `• ${error}`).join('\n')
      );
      return;
    }

    setLoading(true);

    // PRD §5.1 — `auth_set_password_submitted` is fired only on the bounded
    // success/failure branches below; the helper's `result` enum is
    // `success | failed`, so an "intent" fire with a third enum value
    // would violate the dictionary. Submit-volume can be derived from
    // `success + failed` in the dashboard.

    try {
      console.log('🔐 Setting password using verification token for:', email);
      
      // OPTIMIZATION: Retrieve onboarding data from AsyncStorage BEFORE API call
      console.log('📥 Retrieving onboarding data from temporary storage...');
      let onboardingData = null;
      try {
        const tempData = await TempOnboardingService.getDataStrict();
        
        if (tempData.selectedClinicId || tempData.name || tempData.interests || tempData.gender || tempData.supportRecipient || tempData.sponsorshipPreference || tempData.addictionTypes || tempData.addictionFrequency || tempData.appGoal) {
          onboardingData = {
            name: tempData.name,
            interests: tempData.interests,
            gender: tempData.gender,
            support_recipient: tempData.supportRecipient,
            sponsorship_preference: tempData.sponsorshipPreference,
            addiction_types: tempData.addictionTypes,
            addiction_frequency: tempData.addictionFrequency,
            app_goal: tempData.appGoal,
            ...clinicSelectionPayload(tempData),
          };
          console.log('📦 Onboarding data retrieved for server-side save:', {
            hasName: !!onboardingData.name,
            hasInterests: !!onboardingData.interests,
            hasGender: !!onboardingData.gender,
            hasSupportRecipient: !!onboardingData.support_recipient,
            hasSponsorshipPreference: onboardingData.sponsorship_preference !== undefined,
            hasAddictionTypes: !!onboardingData.addiction_types,
            hasAddictionFrequency: !!onboardingData.addiction_frequency,
            hasAppGoal: !!onboardingData.app_goal
          });
        } else {
          console.log('ℹ️ No onboarding data found in temporary storage');
        }
      } catch (onboardingError) {
        if (onboardingError instanceof ClinicSelectionError) throw onboardingError;
        throw new ClinicSelectionError('ONBOARDING_INCOMPLETE');
      }

      await ensureClinicCompletionSupport();
      // Call set-password API endpoint with onboarding data
      console.log('📡 Making API call to set-password endpoint...');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      let response;
      try {
        response = await fetch('https://mobile.padrinho.app/api/auth/set-password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            verificationToken: verificationToken,
            password: password,
            onboardingData: onboardingData // Send to server for single-transaction save
          }),
          signal: controller.signal
        });
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          console.error('❌ Request timeout after 30 seconds');
          throw new Error('Tempo esgotado. Por favor, tente novamente.');
        }
        console.error('❌ Network error:', fetchError);
        throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
      }
      
      clearTimeout(timeoutId);
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('❌ Failed to parse server response:', parseError);
        throw new Error('Resposta inválida do servidor');
      }

      if (!response.ok || !data.success) {
        const completionError = clinicCompletionError(data.code, response.status);
        if (completionError) throw completionError;
        console.error('❌ Set password API failed:', data);
        throw new Error(data.error || 'Erro ao definir senha');
      }

      // PRD §5.1 — `auth_set_password_submitted` success outcome.
      import('@/utils/clarityEvents')
        .then(({ logAuthSetPasswordSubmitted }) =>
          logAuthSetPasswordSubmitted({ result: 'success' })
        )
        .catch(() => {});

      console.log('✅ Password set successfully via API (onboarding data saved server-side)');
      console.log('📦 Server response:', {
        hasSession: !!data.session,
        hasUser: !!data.user,
        sessionKeys: data.session ? Object.keys(data.session) : [],
        userEmail: data.user?.email
      });

      // Establish Supabase session using the returned session tokens
      const { session, user: authUser } = data;
      
      if (!session || !authUser) {
        console.error('❌ Session or user not received from server:', data);
        throw new Error('Sessão não recebida do servidor');
      }

      console.log('🔐 Setting session with tokens:', {
        hasAccessToken: !!session.access_token,
        hasRefreshToken: !!session.refresh_token,
        tokenLength: session.access_token?.length,
        newUserId: authUser.id,
        newUserEmail: authUser.email
      });

      // Set onboarding flag FIRST to prevent AuthGuard interference
      console.log('🚩 Setting onboarding flag...');
      await OnboardingStateService.setOnboardingInProgress(data.onboardingPending !== true);
      await OnboardingStateService.setAuthenticatedPending(authUser.id, data.onboardingPending === true);

      // Biometric enrolment prompt is no longer shown automatically anywhere
      // except (a) login-with-saved-credentials and (b) manual toggle in the
      // Profile screen. Nothing to suppress here.

      // CRITICAL: write the consent-skip flag BEFORE signInWithPassword.
      // signInWithPassword triggers onAuthStateChange → AuthContext loads
      // the profile → StartupPromptContext builds its queue. If we wrote
      // the flag only after the consent record write below, buildQueue
      // could race ahead and enqueue the consent sheet for a user that
      // just accepted terms. The flag must already be on disk by the time
      // the queue is built. Gated on `termsAccepted` because that's the
      // signal that consent will in fact be recorded next.
      if (termsAccepted) {
        try {
          await AsyncStorage.setItem(CONSENT_PROMPT_SKIP_KEY, 'true');
        } catch {
          // non-blocking
        }
      }

      // Use signInWithPassword instead of setSession to avoid deadlock
      // We have the email and password, so we can authenticate normally
      console.log('🔐 Signing in with password...');
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      // Track Meta `CompleteRegistration` only on a fully successful signup —
      // i.e. (a) sign-in succeeded AND (b) when terms were accepted, the consent
      // record write also succeeded. This protects attribution quality: a failed
      // sign-in or a consent-write error must NOT count as a completed
      // registration in Meta Ads Manager.
      let registrationEventEligible = false;

      if (signInError) {
        console.error('❌ Sign in error:', signInError);
        console.warn('⚠️ Could not auto-login, but account was created successfully');
      } else {
        console.log('✅ User signed in successfully:', signInData.user?.id);

        let consentRecorded = !termsAccepted; // if no terms required, nothing to record
        if (termsAccepted && signInData.session?.access_token) {
          try {
            const versionsRes = await fetch('https://mobile.padrinho.app/api/consent/current-version');
            const versionsData = await versionsRes.json();
            const termsVersion = versionsData.terms_version || '1.0';
            const privacyVersion = versionsData.privacy_version || '1.0';

            await fetch('https://mobile.padrinho.app/api/consent/record', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${signInData.session.access_token}`
              },
              body: JSON.stringify({
                consents: [
                  { consent_type: 'terms_of_service', version: termsVersion, granted: true },
                  { consent_type: 'privacy_policy', version: privacyVersion, granted: true }
                ]
              })
            });
            console.log('✅ Consent recorded for new user');
            consentRecorded = true;

            // Note: CONSENT_PROMPT_SKIP_KEY was written above, before
            // signInWithPassword, to win the race against the startup
            // queue. No need to set it again here.

            // PRD §4 — `consent_accepted` macro event. Only fires on the
            // happy path where the consent record write succeeded.
            import('@/utils/clarityEvents')
              .then(({ logConsentAccepted }) =>
                logConsentAccepted({
                  terms_version: termsVersion,
                  privacy_version: privacyVersion,
                })
              )
              .catch(() => {});
          } catch (consentError) {
            console.warn('⚠️ Could not record consent (non-blocking):', consentError);
            consentRecorded = false;
          }
        }

        registrationEventEligible = consentRecorded;
      }

      if (data.onboardingPending === true) {
        // An old invitation link may set a password before collecting answers.
        // Keep the draft and do not count an unfinished signup as completed.
        setLoading(false);
        router.replace('/onboarding-welcome');
        return;
      }
      TempOnboardingService.clearData().catch(() => {});

      // Fire attribution events ONLY on the fully-successful path (sign-in +
      // consent both ok). Both services no-op when the SDK isn't ready.
      if (registrationEventEligible) {
        try {
          const { FacebookService } = await import('../services/facebookService');
          FacebookService.logCompleteRegistration('email');
        } catch (fbError) {
          console.warn('⚠️ Facebook CompleteRegistration log failed (non-blocking):', fbError);
        }

        // PRD §4 — `auth_signup_completed` macro event. Fires on the same
        // happy path as Meta CompleteRegistration so the two attribution
        // surfaces stay aligned. The helper itself fans out to Firebase
        // Analytics (`sign_up` standard event) — see utils/clarityEvents.ts
        // and SYS_GOOGLE_ADS_SDK.md.
        import('@/utils/clarityEvents')
          .then(({ logAuthSignupCompleted }) =>
            logAuthSignupCompleted({ method: 'email' })
          )
          .catch(() => {});
      } else {
        console.log('📘 [Facebook] CompleteRegistration skipped — signup not fully successful');
      }

      // Navigate to onboarding-success
      console.log('✅ Password setup complete - navigating to onboarding-success');
      
      setLoading(false);
      router.replace('/onboarding-success');

    } catch (error) {
      if (error instanceof ClinicSelectionError) {
        setLoading(false);
        const feedback = clinicCompletionFeedback(error.code);
        Alert.alert(t('onboarding.clinic.errorTitle'),
          t(feedback.messageKey),
          feedback.route ? [
            { text: t(feedback.actionKey), onPress: () => router.push(feedback.route!) },
          ] : undefined);
        return;
      }
      console.error('❌ Error creating account:', error);
      // PRD §5.1 — `auth_set_password_submitted` failure outcome (covers
      // weak password, expired token, network errors, etc — the bounded
      // dictionary doesn't enumerate sub-reasons here, only the outcome).
      import('@/utils/clarityEvents')
        .then(({ logAuthSetPasswordSubmitted }) =>
          logAuthSetPasswordSubmitted({ result: 'failed' })
        )
        .catch(() => {});
      console.error('Full error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Handle specific errors with user-friendly messages
      let errorMessage = t('auth.setPassword.errorSetPassword');
      let shouldRedirectToFirstAccess = false;
      
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        
        if (errorMsg.includes('token de verificação inválido') || 
            errorMsg.includes('token') && errorMsg.includes('expirado') ||
            errorMsg.includes('jwt expired')) {
          errorMessage = t('auth.setPassword.errorSessionExpired');
          shouldRedirectToFirstAccess = true;
        } else if (errorMsg.includes('weak_password') || errorMsg.includes('senha fraca')) {
          errorMessage = t('auth.setPassword.errorWeakPassword');
        } else if (errorMsg.includes('network') || errorMsg.includes('conexão')) {
          errorMessage = t('auth.setPassword.errorNetwork');
        } else if (errorMsg.includes('timeout') || errorMsg.includes('tempo esgotado')) {
          errorMessage = t('auth.setPassword.errorTimeout');
        } else if (errorMsg.includes('sessão não recebida')) {
          errorMessage = t('auth.setPassword.errorSessionNotReceived');
        } else {
          // Use the original error message if it's user-friendly enough
          errorMessage = error.message.length < 100 ? error.message : t('auth.setPassword.errorGeneric');
        }
      }
      
      Alert.alert(
        t('auth.setPassword.errorTitle'), 
        errorMessage,
        shouldRedirectToFirstAccess ? [
          { 
            text: 'OK', 
            onPress: () => {
              console.log('🔄 Redirecting to first-access due to error...');
              router.replace('/first-access');
            }
          }
        ] : [{ text: 'OK' }]
      );
    } finally {
      setLoading(false);
    }
  };

  // Get password validation status
  const validation = validatePassword(password);
  const progressWidth = 84; // Step 7 of 7

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView 
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
        {/* Step Section with BLACK badge */}
        <View style={styles.section}>
          <View style={styles.stepBadge}>
            <Text style={styles.stepNumber}>7</Text>
          </View>

          <View style={styles.textContainer}>
            <Text style={styles.title}>{t('auth.setPassword.title')}</Text>
            <Text style={styles.subtitle}>
              {t('auth.setPassword.subtitle')}
            </Text>
          </View>
        </View>

        {/* Password Fields Section */}
        <View style={styles.fieldsSection}>

            <View style={styles.field}>
              <Text style={styles.label}>{t('auth.setPassword.password')}</Text>
              <View style={styles.passwordFieldWrapper}>
                <TextInput
                  style={styles.input}
                  placeholder={t('auth.setPassword.placeholder')}
                  placeholderTextColor="#A19F97"
                  color="#151515"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity 
                  style={styles.eyeButton}
                  onPress={() => setShowPassword(!showPassword)}
                >
                  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <Path
                      d="M15.58 12c0 1.98-1.6 3.58-3.58 3.58S8.42 13.98 8.42 12s1.6-3.58 3.58-3.58 3.58 1.6 3.58 3.58Z"
                      stroke="#757C85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      d="M12 20.27c3.53 0 6.82-2.08 9.11-5.68.9-1.41.9-3.78 0-5.19C19.82 5.81 16.53 3.73 12 3.73S4.18 5.81 1.89 9.4c-.9 1.41-.9 3.78 0 5.19C4.18 18.19 7.47 20.27 12 20.27Z"
                      stroke="#757C85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {!showPassword && (
                      <Path
                        d="m4.5 4.5 15 15"
                        stroke="#757C85"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </Svg>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>{t('auth.setPassword.confirmPassword')}</Text>
              <View style={styles.passwordFieldWrapper}>
                <TextInput
                  style={styles.input}
                  placeholder={t('auth.setPassword.placeholder')}
                  placeholderTextColor="#A19F97"
                  color="#151515"
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  secureTextEntry={!showConfirmPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity 
                  style={styles.eyeButton}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <Path
                      d="M15.58 12c0 1.98-1.6 3.58-3.58 3.58S8.42 13.98 8.42 12s1.6-3.58 3.58-3.58 3.58 1.6 3.58 3.58Z"
                      stroke="#757C85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      d="M12 20.27c3.53 0 6.82-2.08 9.11-5.68.9-1.41.9-3.78 0-5.19C19.82 5.81 16.53 3.73 12 3.73S4.18 5.81 1.89 9.4c-.9 1.41-.9 3.78 0 5.19C4.18 18.19 7.47 20.27 12 20.27Z"
                      stroke="#757C85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {!showConfirmPassword && (
                      <Path
                        d="m4.5 4.5 15 15"
                        stroke="#757C85"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </Svg>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.requirementsContainer}>
              <Text style={styles.requirementsTitle}>{t('auth.setPassword.requirementsTitle')}</Text>

              <View style={styles.requirementsList}>
                <View style={styles.requirementItem}>
                  <View style={[
                    styles.checkbox,
                    validation.checks.minLength && styles.checkboxActive
                  ]}>
                    {validation.checks.minLength && (
                      <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <Path 
                          d="M6.33268 2.396L3.12435 5.60433L1.66602 4.146" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                        />
                      </Svg>
                    )}
                  </View>
                  <Text style={styles.requirementText}>{t('auth.setPassword.requirementMinLength')}</Text>
                </View>

                <View style={styles.requirementItem}>
                  <View style={[
                    styles.checkbox,
                    validation.checks.hasUppercase && styles.checkboxActive
                  ]}>
                    {validation.checks.hasUppercase && (
                      <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <Path 
                          d="M6.33268 2.396L3.12435 5.60433L1.66602 4.146" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                        />
                      </Svg>
                    )}
                  </View>
                  <Text style={styles.requirementText}>{t('auth.setPassword.requirementUppercase')}</Text>
                </View>

                <View style={styles.requirementItem}>
                  <View style={[
                    styles.checkbox,
                    validation.checks.hasLowercase && styles.checkboxActive
                  ]}>
                    {validation.checks.hasLowercase && (
                      <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <Path 
                          d="M6.33268 2.396L3.12435 5.60433L1.66602 4.146" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                        />
                      </Svg>
                    )}
                  </View>
                  <Text style={styles.requirementText}>{t('auth.setPassword.requirementLowercase')}</Text>
                </View>

                <View style={styles.requirementItem}>
                  <View style={[
                    styles.checkbox,
                    validation.checks.hasNumber && styles.checkboxActive
                  ]}>
                    {validation.checks.hasNumber && (
                      <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <Path 
                          d="M6.33268 2.396L3.12435 5.60433L1.66602 4.146" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                        />
                      </Svg>
                    )}
                  </View>
                  <Text style={styles.requirementText}>{t('auth.setPassword.requirementNumber')}</Text>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>

        <View style={styles.buttonContainer}>
          <PrimaryButton
            title={loading ? t('auth.setPassword.buttonLoading') : t('auth.setPassword.button')}
            onPress={handleSetPassword}
            disabled={loading || !validation.isValid || password !== confirmPassword}
            loading={loading}
          />
        </View>
      </KeyboardAvoidingView>
    </OnboardingLayout>
  );
}

interface Styles {
  keyboardAvoidingView: ViewStyle;
  scrollView: ViewStyle;
  section: ViewStyle;
  stepBadge: ViewStyle;
  stepNumber: TextStyle;
  textContainer: ViewStyle;
  title: TextStyle;
  subtitle: TextStyle;
  fieldsSection: ViewStyle;
  field: ViewStyle;
  label: TextStyle;
  passwordFieldWrapper: ViewStyle;
  input: TextStyle;
  eyeButton: ViewStyle;
  requirementsContainer: ViewStyle;
  requirementsTitle: TextStyle;
  requirementsList: ViewStyle;
  requirementItem: ViewStyle;
  checkbox: ViewStyle;
  checkboxActive: ViewStyle;
  requirementText: TextStyle;
  buttonContainer: ViewStyle;
  termsText: TextStyle;
  termsLink: TextStyle;
  inlineButton: ViewStyle;
  authLoadingContainer: ViewStyle;
  spinner: ViewStyle;
  authLoadingTitle: TextStyle;
  authLoadingStatus: TextStyle;
  authLoadingSubtitle: TextStyle;
}

const styles = StyleSheet.create<Styles>({
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  } as ViewStyle,
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  } as ViewStyle,
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  subtitle: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    width: 345,
  },
  fieldsSection: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 24,
  } as ViewStyle,
  field: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 12,
    flexDirection: 'column',
    width: '100%',
  } as ViewStyle,
  label: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14,
  },
  passwordFieldWrapper: {
    width: '100%',
    position: 'relative',
  } as ViewStyle,
  input: {
    width: '100%',
    height: 48,
    paddingVertical: 12,
    paddingHorizontal: 12,
    paddingRight: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 20,
    color: '#151515',
  },
  eyeButton: {
    position: 'absolute',
    right: 12,
    top: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  } as ViewStyle,
  requirementsContainer: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 20,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#F0EEE4',
    borderRadius: 16,
    flexDirection: 'column',
    width: '100%',
  } as ViewStyle,
  requirementsTitle: {
    color: '#000000',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14,
  },
  requirementsList: {
    alignItems: 'flex-start',
    gap: 8,
    flexDirection: 'column',
    width: '100%',
  } as ViewStyle,
  requirementItem: {
    alignItems: 'center',
    gap: 8,
    width: '100%',
    height: 24,
    flexDirection: 'row',
  } as ViewStyle,
  checkbox: {
    aspectRatio: 1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  checkboxActive: {
    borderWidth: 3,
    borderColor: '#0FA971',
    backgroundColor: '#0FA971',
    padding: 4,
  },
  requirementText: {
    color: '#151515',
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6, // 140%
  },
  buttonContainer: {
    padding: 24,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    minHeight: 96, // Ensure minimum container height
  } as ViewStyle,
  
  termsText: {
    color: '#757C85',
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    lineHeight: 16.8, // 140%
    textAlign: 'center',
  },
  termsLink: {
    color: '#015480',
    textDecorationLine: 'underline',
  },
  inlineButton: {
    flexDirection: 'row',
  },
  
  // Authentication loading styles
  authLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  spinner: {
    marginBottom: 24,
  },
  authLoadingTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    marginBottom: 16,
  },
  authLoadingStatus: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#015480',
    textAlign: 'center',
    marginBottom: 8,
  },
  authLoadingSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#666666',
    textAlign: 'center',
    lineHeight: 20,
  },
});